import { useState, useEffect } from 'react';
import { Calendar, Clock, Info, AlertCircle, Loader2, ArrowLeft } from 'lucide-react';
import API_BASE_URL from '../../config';

const FreezeScreen = ({ onBack, gymCode }) => {
  const [settings, setSettings] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);

  // Form state
  const [startDate, setStartDate] = useState('');
  const [duration, setDuration] = useState('');
  const [reason, setReason] = useState('');

  const [banner, setBanner] = useState({ type: null, message: '' });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gymCode]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = JSON.parse(localStorage.getItem('accessToken') || '{}').token;
      
      // 1. Fetch settings
      const settingsRes = await fetch(`${API_BASE_URL}/api/membershipfreeze/settings?gymCode=${gymCode}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      let settingsData = null;
      if (settingsRes.ok) {
        settingsData = await settingsRes.json();
        setSettings(settingsData);
      }

      // 2. Fetch history
      const historyRes = await fetch(`${API_BASE_URL}/api/membershipfreeze/history?gymCode=${gymCode}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (historyRes.ok) {
        const historyData = await historyRes.json();
        setHistory(historyData);
      }
    } catch (err) {
      console.error('Failed to load freeze data:', err);
    } finally {
      setLoading(false);
    }
  };

  const showBannerMessage = (type, message) => {
    setBanner({ type, message });
    setTimeout(() => setBanner({ type: null, message: '' }), 4000);
  };

  // Calculate remaining allowance in trailing anniversary year
  const calculateRemainingAllowance = () => {
    if (!settings) return 0;
    
    // Sum up Approved freezes in current anniversary year.
    // Wait, since we are doing this client side, let's sum up approved requests whose RequestedStartDate is within the past 365 days.
    // (Or we can retrieve it exactly, but summing up approved requests in history is a good approximation, or we can rely on backend check).
    const approvedFreezes = history.filter(r => r.status === 1); // Status 1 = Approved
    
    // Anniversary anchor calculation is best done by the server. So the server will validate and throw error if limit exceeded.
    // But we can show a nice helper of days used in history.
    const totalUsed = approvedFreezes.reduce((sum, r) => sum + (r.actualDurationDays || r.durationDays), 0);
    return Math.max(0, settings.maxFreezeDaysPerYear - totalUsed);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!startDate || !duration) {
      showBannerMessage('error', 'Start date and duration are required.');
      return;
    }

    if (Number(duration) < settings.minFreezeDurationDays || Number(duration) > settings.maxFreezeDurationDays) {
      showBannerMessage('error', `Duration must be between ${settings.minFreezeDurationDays} and ${settings.maxFreezeDurationDays} days.`);
      return;
    }

    setSubmitting(true);
    try {
      const token = JSON.parse(localStorage.getItem('accessToken') || '{}').token;
      const response = await fetch(`${API_BASE_URL}/api/membershipfreeze/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          gymCode,
          startDate,
          durationDays: Number(duration),
          reason
        })
      });

      if (response.ok) {
        showBannerMessage('success', 'Freeze request submitted successfully!');
        setStartDate('');
        setDuration('');
        setReason('');
        // Reload history
        const historyRes = await fetch(`${API_BASE_URL}/api/membershipfreeze/history?gymCode=${gymCode}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (historyRes.ok) {
          const historyData = await historyRes.json();
          setHistory(historyData);
        }
      } else {
        const errText = await response.text();
        showBannerMessage('error', errText || 'Failed to submit freeze request.');
      }
    } catch (err) {
      console.error('Submit freeze request error:', err);
      showBannerMessage('error', 'An error occurred.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelRequest = async (id) => {
    setCancellingId(id);
    try {
      const token = JSON.parse(localStorage.getItem('accessToken') || '{}').token;
      const response = await fetch(`${API_BASE_URL}/api/membershipfreeze/cancel/${id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        showBannerMessage('success', 'Request cancelled successfully.');
        setHistory(prev => prev.map(r => r.id === id ? { ...r, status: 3 } : r)); // Status 3 = Cancelled
      } else {
        const errText = await response.text();
        showBannerMessage('error', errText || 'Failed to cancel request.');
      }
    } catch (err) {
      console.error('Cancel request error:', err);
      showBannerMessage('error', 'An error occurred.');
    } finally {
      setCancellingId(null);
    }
  };

  const activeOrPendingRequest = history.find(r => 
    r.status === 0 || // Pending
    (r.status === 1 && new Date(r.computedEndDate) > new Date()) // Approved & Active/Scheduled
  );

  const getStatusBadge = (status) => {
    switch (status) {
      case 0: return <span className="px-2 py-0.5 text-[9px] font-bold uppercase rounded bg-yellow-500/10 text-yellow-400 border border-yellow-500/20">Pending</span>;
      case 1: return <span className="px-2 py-0.5 text-[9px] font-bold uppercase rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">Approved</span>;
      case 2: return <span className="px-2 py-0.5 text-[9px] font-bold uppercase rounded bg-red-500/10 text-red-400 border border-red-500/20">Rejected</span>;
      case 3: return <span className="px-2 py-0.5 text-[9px] font-bold uppercase rounded bg-gray-500/10 text-gray-400 border border-gray-500/20">Cancelled</span>;
      default: return null;
    }
  };

  const todayStr = new Date().toISOString().split('T')[0];

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-2 bg-[#222] border border-[#2b2b2b] hover:bg-[#2b2b2b] rounded-lg text-gray-400 hover:text-white transition-all">
          <ArrowLeft size={18} />
        </button>
        <div>
          <h2 className="text-xl font-bold text-white">Membership Freeze</h2>
          <p className="text-xs text-gray-400">Request a temporary pause on your membership plan</p>
        </div>
      </div>

      {/* Banner */}
      {banner.message && (
        <div className={`p-4 rounded-xl border flex items-center gap-2 ${banner.type === 'success' ? 'bg-[#6CD800]/10 border-[#6CD800]/25 text-[#6CD800]' : 'bg-red-500/10 border-red-500/25 text-red-500'}`}>
          <Info size={16} />
          <p className="text-xs font-semibold">{banner.message}</p>
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <Loader2 className="animate-spin text-[#6CD800] mb-3" size={32} />
          <p className="text-gray-400 text-xs font-medium">Loading freeze settings...</p>
        </div>
      ) : !settings?.isFreezeEnabled ? (
        <div className="bg-[#1c1c1c] border border-[#2b2b2b] rounded-2xl p-6 text-center space-y-4">
          <AlertCircle size={40} className="text-yellow-500 mx-auto" />
          <h3 className="text-sm font-bold text-white">Freeze Feature Disabled</h3>
          <p className="text-xs text-gray-400 max-w-xs mx-auto leading-relaxed">
            Membership freezing is not enabled at your gym. Please contact support or your gym owner if you think this is a mistake.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Info Card */}
          <div className="bg-lime-950/20 border border-lime-500/30 rounded-2xl p-4 flex gap-3 items-start">
            <Info className="text-[#6CD800] shrink-0 mt-0.5" size={16} />
            <div className="space-y-1">
              <h4 className="text-xs font-bold text-white uppercase tracking-wider">Freeze Guidelines</h4>
              <p className="text-[11px] text-gray-300 leading-relaxed">
                You can pause your plan for a minimum of <span className="font-semibold text-white">{settings.minFreezeDurationDays} days</span> and a maximum of <span className="font-semibold text-white">{settings.maxFreezeDurationDays} days</span> per request.
                Remaining allowance this anniversary year: <span className="font-bold text-[#6CD800]">{calculateRemainingAllowance()} Days</span>.
              </p>
            </div>
          </div>

          {/* Active / Pending Request */}
          {activeOrPendingRequest && (
            <div className="bg-[#1c1c1c] border border-[#2b2b2b] rounded-2xl p-5 space-y-4">
              <div className="flex justify-between items-center">
                <h4 className="text-sm font-bold text-white">Active/Scheduled Request</h4>
                {getStatusBadge(activeOrPendingRequest.status)}
              </div>

              <div className="grid grid-cols-2 gap-3 bg-[#101010] p-3 rounded-xl border border-[#222]">
                <div>
                  <span className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">Start Date</span>
                  <span className="text-xs font-semibold text-white flex items-center gap-1.5 mt-1">
                    <Calendar size={12} className="text-[#6CD800]" />
                    {new Date(activeOrPendingRequest.requestedStartDate).toLocaleDateString()}
                  </span>
                </div>
                <div>
                  <span className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">Duration</span>
                  <span className="text-xs font-semibold text-white flex items-center gap-1.5 mt-1">
                    <Clock size={12} className="text-[#6CD800]" />
                    {activeOrPendingRequest.durationDays} Days
                  </span>
                </div>
              </div>

              {/* Cancel Button */}
              {activeOrPendingRequest.status === 0 && (
                <button
                  onClick={() => handleCancelRequest(activeOrPendingRequest.id)}
                  disabled={cancellingId === activeOrPendingRequest.id}
                  className="w-full py-2.5 bg-red-600/10 hover:bg-red-600 hover:text-white border border-red-500/20 text-red-500 font-bold rounded-xl text-xs transition-all cursor-pointer"
                >
                  {cancellingId === activeOrPendingRequest.id ? 'Cancelling...' : 'Cancel Pending Request'}
                </button>
              )}

              {activeOrPendingRequest.status === 1 && new Date(activeOrPendingRequest.requestedStartDate) > new Date() && (
                <button
                  onClick={() => handleCancelRequest(activeOrPendingRequest.id)}
                  disabled={cancellingId === activeOrPendingRequest.id}
                  className="w-full py-2.5 bg-red-600/10 hover:bg-red-600 hover:text-white border border-red-500/20 text-red-500 font-bold rounded-xl text-xs transition-all cursor-pointer"
                >
                  {cancellingId === activeOrPendingRequest.id ? 'Cancelling...' : 'Cancel Scheduled Freeze'}
                </button>
              )}
            </div>
          )}

          {/* Freeze Form */}
          {!activeOrPendingRequest && (
            <form onSubmit={handleSubmit} className="bg-[#161616] border border-[#2b2b2b] rounded-2xl p-5 space-y-4">
              <h3 className="text-sm font-bold text-white border-b border-[#252525] pb-2 mb-2">Submit Freeze Request</h3>

              <div>
                <label className="block text-gray-400 text-[10px] uppercase tracking-widest font-bold mb-2">Freeze Start Date</label>
                <input
                  type="date"
                  min={todayStr}
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-4 py-2.5 bg-[#0d0d0d] border border-[#2b2b2b] rounded-xl text-xs text-white outline-none focus:border-[#6CD800]/50 transition-colors"
                  required
                />
              </div>

              <div>
                <label className="block text-gray-400 text-[10px] uppercase tracking-widest font-bold mb-2">Duration (Days)</label>
                <input
                  type="number"
                  min={settings.minFreezeDurationDays}
                  max={settings.maxFreezeDurationDays}
                  placeholder={`Min ${settings.minFreezeDurationDays}, Max ${settings.maxFreezeDurationDays}`}
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  className="w-full px-4 py-2.5 bg-[#0d0d0d] border border-[#2b2b2b] rounded-xl text-xs text-white outline-none focus:border-[#6CD800]/50 transition-colors"
                  required
                />
              </div>

              <div>
                <label className="block text-gray-400 text-[10px] uppercase tracking-widest font-bold mb-2">Reason</label>
                <textarea
                  rows={2}
                  placeholder="Tell us why you need to freeze..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full px-4 py-2.5 bg-[#0d0d0d] border border-[#2b2b2b] rounded-xl text-xs text-white outline-none focus:border-[#6CD800]/50 transition-colors resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={submitting || calculateRemainingAllowance() <= 0}
                className="w-full py-3 bg-[#6CD800] hover:bg-[#5bb300] disabled:opacity-50 text-zinc-950 font-bold rounded-xl text-xs transition-colors cursor-pointer"
              >
                {submitting ? 'Submitting...' : 'Request Freeze'}
              </button>
            </form>
          )}

          {/* History */}
          {history.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-bold text-white px-1">Freeze History</h3>
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {history.map((req) => (
                  <div key={req.id} className="bg-[#161616] border border-[#2b2b2b] rounded-xl p-3.5 flex justify-between items-center gap-3">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-white">
                          {req.durationDays} Days Freeze
                        </span>
                        {getStatusBadge(req.status)}
                      </div>
                      <p className="text-[10px] text-gray-500">
                        Requested: {new Date(req.requestedStartDate).toLocaleDateString()}
                      </p>
                      {req.rejectionReason && (
                        <p className="text-[10px] text-red-400 mt-1">
                          Cause: "{req.rejectionReason}"
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FreezeScreen;
